import React, { useEffect, useMemo, useState } from "react";
import { Table } from "antd";
import { useRecoilValue } from "recoil";
import DateRangePickerComponent from "../dashboard/components/Shared/DateRangePickerComponent";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import MemoryType from "../../../service/memory/enum/MemoryType";
import { officeLabel } from "../../../components/recoil/officeLabelRecoil";
import { MultiSelect } from "../../generic/elements/multiselect/MultiSelect";
import apiService from "../../../service/apiservice/apiService";
import {
  convertDateToEODTimestamp,
  convertDateToSODTimestamp,
  dateAndTime,
  getDateString,
} from "../../../utils/dateutils";
import { formatThousands } from "./utils";

const today = new Date();

const defaultDateRange = [
  {
    startDate: new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - 30,
    ),
    endDate: new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
    ),
    key: "selection",
  },
];

const formatDate = (timestamp) => (timestamp ? getDateString(timestamp) : "");

const formatDateTime = (timestamp) => (timestamp ? dateAndTime(timestamp) : "");

const formatHours = (value) =>
  value == null || value === "" ? "" : formatThousands(value);

const formatVolunteers = (volunteers) => {
  if (Array.isArray(volunteers)) return volunteers.filter(Boolean).join(", ");
  return volunteers ?? "";
};

const columns = [
  {
    title: "Patient Name",
    dataIndex: "patientName",
    key: "patientName",
  },
  {
    title: "Team",
    dataIndex: "team",
    key: "team",
  },
  {
    title: "Coordinator",
    dataIndex: "coordinator",
    key: "coordinator",
  },
  {
    title: "Requested Date",
    dataIndex: "requestedDate",
    key: "requestedDate",
    render: formatDate,
  },
  {
    title: "Requested Start",
    dataIndex: "requestedStartTime",
    key: "requestedStartTime",
    render: formatDateTime,
  },
  {
    title: "Requested End",
    dataIndex: "requestedEndTime",
    key: "requestedEndTime",
    render: formatDateTime,
  },
  {
    title: "Total Hours Requested",
    dataIndex: "totalHoursRequested",
    key: "totalHoursRequested",
    render: formatHours,
  },
  {
    title: "Hours Utilized",
    dataIndex: "hoursUtilized",
    key: "hoursUtilized",
    render: formatHours,
  },
  {
    title: "Volunteers",
    dataIndex: "volunteers",
    key: "volunteers",
    render: formatVolunteers,
  },
];

const EleventhHourReporting = ({ onExportDataChange }) => {
  const organizationIdentifier = useGetOrgIdentifier();
  const userDetails = useReadMemory(MemoryType.USER);
  const label = useRecoilValue(officeLabel);
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [, setIsDefaultDateRange] = useState(true);
  const [showResetBtn, setShowResetBtn] = useState(false);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const teamsOptions = useMemo(
    () =>
      userDetails?.teams
        ?.filter(
          (team) =>
            team?.organizationIdentifier === userDetails?.organizationIdentifier,
        )
        ?.map((team) => ({
          ...team,
          label: team?.name,
          value: team?.id,
        })) ?? [],
    [userDetails?.organizationIdentifier, userDetails?.teams],
  );

  useEffect(() => {
    const mapped = teamsOptions.map((team) => ({
      ...team,
      name: team.label,
      id: team.value,
    }));
    if (!mapped.length) {
      setSelectedTeams([]);
      return;
    }
    setSelectedTeams((prev) => {
      if (!prev?.length) return mapped;
      const validIds = new Set(mapped.map((team) => String(team.value)));
      const stillValid = prev.filter((team) =>
        validIds.has(String(team.value)),
      );
      return stillValid.length === prev.length ? prev : mapped;
    });
  }, [teamsOptions]);

  const handleTeamSelect = (selectedValues) => {
    const finalSelection = !selectedValues?.length
      ? teamsOptions
      : selectedValues;
    const transformedTeams = finalSelection.map((team) => ({
      ...team,
      name: team.label,
      id: team.value,
    }));
    const isSame =
      transformedTeams.length === selectedTeams.length &&
      transformedTeams.every((team, index) => team.id === selectedTeams[index]?.id);
    if (!isSame) setSelectedTeams(transformedTeams);
  };

  useEffect(() => {
    if (!organizationIdentifier) return undefined;

    const teamIds = selectedTeams
      .map((team) => team?.value ?? team?.id)
      .filter((id) => id != null && id !== "");
    if (teamsOptions.length && !teamIds.length) return undefined;

    let cancelled = false;

    const fetchEleventhHourReport = async () => {
      setLoading(true);
      try {
        const params = {
          organizationIdentifier,
          startTime: convertDateToSODTimestamp(dateRange[0].startDate),
          endTime: convertDateToEODTimestamp(dateRange[0].endDate),
        };
        if (teamIds.length) {
          params.teamIds = teamIds.join(",");
        }

        const resultJson = await apiService.get(
          "niramayafoundation/niramayareporting/v1/report/eleventhHour",
          params,
        );
        if (!cancelled) {
          setRows(Array.isArray(resultJson) ? resultJson : []);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchEleventhHourReport();
    return () => {
      cancelled = true;
    };
  }, [organizationIdentifier, dateRange, selectedTeams, teamsOptions.length]);

  const dataSource = useMemo(
    () =>
      rows.map((row, index) => ({
        ...row,
        key: `${row?.carePlanId ?? "row"}-${row?.requestedStartTime ?? index}`,
      })),
    [rows],
  );

  useEffect(() => {
    if (!onExportDataChange) return undefined;
    onExportDataChange({
      rows,
      startDate: dateRange?.[0]?.startDate ?? null,
      endDate: dateRange?.[0]?.endDate ?? null,
      teams: selectedTeams
        .map((team) => team?.name || team?.label)
        .filter(Boolean),
    });
    return () => onExportDataChange(null);
  }, [onExportDataChange, rows, dateRange, selectedTeams]);

  return (
    <>
      <div className="eleventh-hour-toolbar">
        <div className="event-type-select-wrapper eleventh-hour-teams">
          <div className="event-type-label">{`Select ${label}:`}</div>
          <MultiSelect
            options={teamsOptions}
            value={selectedTeams}
            onChange={handleTeamSelect}
            placeholder={`Click here to select ${label}s`}
            isClearable={false}
            allLabel={`All ${label}s`}
            width="280px"
          />
        </div>
        <div className="eleventh-hour-date-range">
          <DateRangePickerComponent
            setIsDefaultDateRange={setIsDefaultDateRange}
            setDateRange={setDateRange}
            dateRange={dateRange}
            showResetBtn={showResetBtn}
            setShowResetBtn={setShowResetBtn}
          />
        </div>
      </div>
      <Table
        loading={loading}
        className="tableStyling"
        pagination={false}
        dataSource={dataSource}
        columns={columns}
      />
    </>
  );
};

export default EleventhHourReporting;
